'use client'

import Link from 'next/link'
import { Drawer, DrawerContent } from '@/components/ui/drawer'
import { MobileChatPane } from './MobileChatPane'
import { Logo } from '@/components/Logo'
import { HugeiconsIcon } from '@hugeicons/react'
import { Cancel01Icon } from '@hugeicons-pro/core-stroke-rounded'

export type MobileAtriumChatSheetProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  isLoggedIn: boolean
}

const DRAWER_CLASS = [
  '!h-[100dvh] !max-h-[100dvh]',
  '!mt-0 !rounded-none',
  'border-0 bg-atrium-background p-0 outline-none',
  '[&>div:first-child]:!hidden',
].join(' ')

export function MobileAtriumChatSheet({ open, onOpenChange, isLoggedIn }: MobileAtriumChatSheetProps) {
  return (
    <Drawer open={open} onOpenChange={onOpenChange} direction="bottom" snapPoints={undefined} dismissible={false}>
      <DrawerContent className={DRAWER_CLASS}>
        {isLoggedIn ? (
          <MobileChatPane active={open} onBack={() => onOpenChange(false)} />
        ) : (
          /* ── Signed-out auth gate ── */
          <div className="relative flex h-full flex-col items-center justify-center px-8 text-center">
            {/* X close */}
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              aria-label="Close"
              className="absolute right-4 top-4 flex size-9 items-center justify-center rounded-full text-atrium-black/50 transition hover:bg-atrium-content hover:text-atrium-black"
              style={{ top: 'calc(env(safe-area-inset-top) + 12px)' }}
            >
              <HugeiconsIcon icon={Cancel01Icon} className="size-5" />
            </button>

            {/* Logo mark */}
            <div className="mb-5 flex size-[72px] items-center justify-center overflow-hidden rounded-2xl shadow-md">
              <Logo variant="mark" width={72} height={72} className="size-[72px]" />
            </div>

            <h2 className="text-[22px] font-extrabold leading-tight tracking-tight text-atrium-black">
              Sign in to chat with Atrium
            </h2>
            <p className="mt-3 max-w-[300px] text-[15px] leading-relaxed text-atrium-muted">
              Create a free account or sign in to plan, launch, and manage your campaigns with AI.
            </p>

            <div className="mt-7 flex w-full max-w-sm flex-col gap-3">
              <Link
                href="/signup"
                className="flex w-full items-center justify-center rounded-2xl bg-accent-600 px-5 py-[14px] text-[15px] font-bold text-white transition hover:bg-accent-700 active:scale-[0.98]"
              >
                Create a free account
              </Link>
              <Link
                href="/login"
                className="flex w-full items-center justify-center rounded-2xl border-2 border-atrium-border bg-atrium-content px-5 py-[13px] text-[15px] font-semibold text-atrium-black transition hover:bg-atrium-border active:scale-[0.98]"
              >
                Sign in
              </Link>
            </div>

            <p className="mt-6 text-[12px] text-atrium-muted">
              By continuing you agree to Atrium&apos;s{' '}
              <Link href="/terms" className="underline opacity-70 hover:opacity-100">
                Terms of Service
              </Link>.
            </p>
          </div>
        )}
      </DrawerContent>
    </Drawer>
  )
}
